import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import useSWR from "swr";

import { Icon } from "../components/Icon";
import {
  Drawer,
  Empty,
  Field,
  Pill,
  ToastProvider,
  errText,
  relativeTime,
  useToast,
} from "../admin/ui";
import {
  api,
  type AdminCompanyRow,
  type FormAdmin,
  type FormScope,
  type Me,
} from "../services/api";

type Tab = "all" | "published" | "draft";

/** Field count + whether the form routes through the approval engine, both
 *  read off the working schema (draft first, so the list reflects edits). */
function metaOf(form: FormAdmin): { fields: number; routed: boolean } {
  const schema = form.draft_schema ?? form.published_schema;
  return {
    fields: (schema?.fields ?? []).filter((f) => f.type !== "section_header").length,
    routed: Boolean(schema?.approval_form_type_slug),
  };
}

/**
 * Forms catalogue.
 *
 * The catalogue every customer draws from. Who a form is *available* to is
 * edited here (its scope); whether an available form is switched on for one
 * customer stays on that customer's page, which is where the rest of their
 * configuration lives.
 */
export function AdminForms() {
  const { data: me } = useSWR<Me>("/api/me", () => api.me());
  if (!me) return <div className="card">Loading…</div>;

  const allowed =
    me.role === "iquest_admin" || me.role === "service_desk" || me.role === "client_admin";
  if (!allowed) {
    return (
      <div className="card empty-state">
        <p>You don&apos;t have access to this page.</p>
        <p className="empty-state-blurb">
          Form management is reserved for client admins and iQuest staff.
        </p>
      </div>
    );
  }

  return (
    <ToastProvider>
      <AdminFormsInner />
    </ToastProvider>
  );
}

function AdminFormsInner() {
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();

  const { data, isLoading, mutate } = useSWR<FormAdmin[]>("/api/admin/forms", () =>
    api.admin.forms.list({ include_unpublished: true }),
  );
  const { data: companies } = useSWR<AdminCompanyRow[]>(
    "/api/admin/forms/companies",
    () => api.admin.forms.companies(),
  );

  const [tab, setTab] = useState<Tab>("all");
  const [audienceFor, setAudienceFor] = useState<FormAdmin | null>(null);
  const [creating, setCreating] = useState(params.get("new") === "1");

  // The admin home links here with ?new=1 to open the create drawer straight
  // away; drop the param once it's consumed so a refresh doesn't reopen it.
  useEffect(() => {
    if (params.get("new") === "1") {
      setCreating(true);
      const next = new URLSearchParams(params);
      next.delete("new");
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  const forms = useMemo(() => data ?? [], [data]);
  const counts = {
    all: forms.length,
    published: forms.filter((f) => f.is_published).length,
    draft: forms.filter((f) => !f.is_published).length,
  };
  const rows =
    tab === "all" ? forms : forms.filter((f) => (tab === "published") === f.is_published);

  const nameOf = useMemo(() => {
    const byId = new Map((companies ?? []).map((c) => [c.id, c.name]));
    return (id: string) => byId.get(id) ?? "Unknown customer";
  }, [companies]);

  function scopeChips(f: FormAdmin) {
    if (f.scope === "global") return <span className="ad-scope all">All customers</span>;
    const ids =
      f.scope === "single_company" ? (f.company_id ? [f.company_id] : []) : f.audience_company_ids;
    if (ids.length === 0) return <span className="ad-scope">Nobody yet</span>;
    return (
      <>
        {ids.slice(0, 2).map((id) => (
          <span key={id} className="ad-scope">
            {nameOf(id)}
          </span>
        ))}
        {ids.length > 2 && <span className="ad-scope">+{ids.length - 2}</span>}
      </>
    );
  }

  async function remove(form: FormAdmin) {
    if (
      !window.confirm(
        `Delete “${form.title}”? Existing submissions stay, but the form leaves the catalogue.`,
      )
    ) {
      return;
    }
    try {
      await api.admin.forms.remove(form.id);
      await mutate();
      toast(`${form.title} deleted`);
    } catch (e) {
      toast(errText(e, "Could not delete that form"), "warn");
    }
  }

  return (
    <div className="rf-wrap">
      <div className="ad-head">
        <div>
          <div className="ad-eyebrow">Admin</div>
          <h1 className="rf-h1">Forms</h1>
          <p className="rf-sub">
            The catalogue every customer draws from. Build and publish here; switch forms
            on or off per customer from their page.
          </p>
        </div>
        <div className="grp">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setCreating(true)}
          >
            <Icon name="plus" size={15} /> New form
          </button>
        </div>
      </div>

      <div className="ad-tabs">
        {(
          [
            ["all", "All"],
            ["published", "Published"],
            ["draft", "Drafts"],
          ] as [Tab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`ad-tab${tab === key ? " active" : ""}`}
            onClick={() => setTab(key)}
            onFocus={(e) => e.currentTarget.scrollIntoView({ block: "nearest", inline: "nearest" })}
          >
            {label}
            <span className="c">{counts[key]}</span>
          </button>
        ))}
      </div>

      <div className="ad-card">
        {isLoading ? (
          <div style={{ padding: 18 }}>Loading…</div>
        ) : rows.length === 0 ? (
          <Empty
            title="No forms here"
            blurb="Create one to give your users a structured request channel."
          />
        ) : (
          <div className="ad-tablewrap">
            <table className="ad-table">
              <thead>
                <tr>
                  <th>Form</th>
                  <th>Available to</th>
                  <th>Approval</th>
                  <th>Status</th>
                  <th>Updated</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((f) => {
                  const meta = metaOf(f);
                  return (
                    <tr
                      key={f.id}
                      className="clickable"
                      onClick={() => setAudienceFor(f)}
                    >
                      <td>
                        <span className="who">
                          <span className="ad-av tile">
                            <Icon name="doc" />
                          </span>
                          <span>
                            <span className="n">{f.client_facing_title || f.title}</span>
                            <br />
                            <span className="e">
                              {f.category ?? "Uncategorised"} · {meta.fields} field
                              {meta.fields === 1 ? "" : "s"}
                            </span>
                          </span>
                        </span>
                      </td>
                      <td>{scopeChips(f)}</td>
                      <td>
                        {meta.routed ? (
                          <Pill kind="info">Routed</Pill>
                        ) : (
                          <Pill kind="off">Instant</Pill>
                        )}
                      </td>
                      <td>
                        {f.is_published ? (
                          <Pill kind="ok">Published</Pill>
                        ) : (
                          <Pill kind="warn">Draft</Pill>
                        )}
                      </td>
                      <td className="mono">{relativeTime(f.updated_at)}</td>
                      <td className="r" onClick={(e) => e.stopPropagation()}>
                        <Link
                          className="btn btn-ghost btn-sm"
                          to={`/admin/forms/${f.id}/edit`}
                        >
                          Edit
                        </Link>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => void remove(f)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {audienceFor && (
        <AudienceDrawer
          form={audienceFor}
          companies={companies ?? []}
          onClose={() => setAudienceFor(null)}
          onSaved={async () => {
            setAudienceFor(null);
            await mutate();
          }}
        />
      )}

      {creating && (
        <CreateFormDrawer
          onClose={() => setCreating(false)}
          onCreated={async (id) => {
            await mutate();
            navigate(`/admin/forms/${id}/edit`);
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Audience                                                           */
/* ------------------------------------------------------------------ */

function AudienceDrawer({
  form,
  companies,
  onClose,
  onSaved,
}: {
  form: FormAdmin;
  companies: AdminCompanyRow[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const toast = useToast();
  const [scope, setScope] = useState<"global" | "some">(
    form.scope === "global" ? "global" : "some",
  );
  const [selected, setSelected] = useState<string[]>(
    form.scope === "single_company"
      ? form.company_id
        ? [form.company_id]
        : []
      : form.audience_company_ids,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const meta = metaOf(form);

  async function save() {
    if (scope === "some" && selected.length === 0) {
      setError("Pick at least one customer, or make the form global.");
      return;
    }
    setBusy(true);
    setError(null);
    // single_company and multi_company differ only in shape; pick whichever
    // matches the selection so the backend's scope rules stay simple.
    const nextScope: FormScope =
      scope === "global"
        ? "global"
        : selected.length === 1
          ? "single_company"
          : "multi_company";
    try {
      await api.admin.forms.updateDraft(form.id, {
        scope: nextScope,
        company_id: nextScope === "single_company" ? selected[0] : null,
        audience_company_ids: nextScope === "multi_company" ? selected : null,
      });
      await onSaved();
      toast(`Saved ${form.title}`);
    } catch (e) {
      setError(errText(e, "Could not save the audience."));
      setBusy(false);
    }
  }

  return (
    <Drawer
      wide
      eyebrow={form.category ?? "Form"}
      title={form.client_facing_title || form.title}
      desc={`${meta.fields} fields · ${meta.routed ? "needs approval" : "instant"} · ${
        form.is_published ? "published" : "draft"
      }`}
      onClose={onClose}
      footer={
        <>
          <Link className="btn btn-ghost left" to={`/admin/forms/${form.id}/edit`}>
            <Icon name="doc" size={15} /> Open in builder
          </Link>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void save()}
            disabled={busy}
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="ad-eyebrow" style={{ marginBottom: 8 }}>
        Who can use it
      </div>
      <div className="ad-sev" style={{ gridTemplateColumns: "1fr 1fr", marginBottom: 18 }}>
        <label>
          <input
            type="radio"
            name="form-scope"
            checked={scope === "global"}
            onChange={() => setScope("global")}
          />
          <span className="t">All customers</span>
          <span className="s">Shows for everyone unless switched off per customer</span>
        </label>
        <label>
          <input
            type="radio"
            name="form-scope"
            checked={scope === "some"}
            onChange={() => setScope("some")}
          />
          <span className="t">Specific customers</span>
          <span className="s">Only the ones ticked below</span>
        </label>
      </div>

      {scope === "some" && (
        <>
          <div className="ad-eyebrow" style={{ marginBottom: 8 }}>
            Customers
          </div>
          {companies.length === 0 ? (
            <div className="ad-note">No customers to choose from.</div>
          ) : (
            companies.map((c) => (
              <label key={c.id} className="ad-pick" style={{ padding: "8px 12px" }}>
                <input
                  type="checkbox"
                  checked={selected.includes(c.id)}
                  onChange={(e) =>
                    setSelected((prev) =>
                      e.target.checked ? [...prev, c.id] : prev.filter((x) => x !== c.id),
                    )
                  }
                />
                <span className="bd">
                  <span className="t" style={{ fontSize: 13 }}>
                    {c.name}
                  </span>
                </span>
              </label>
            ))
          )}
        </>
      )}

      <div className="ad-note" style={{ marginTop: 16 }}>
        This sets who the form is <em>available</em> to. Switching an available form on or
        off for one customer lives on that customer&apos;s Forms &amp; approvals section.
      </div>

      {error && (
        <div className="form-error" style={{ marginTop: 12 }}>
          {error}
        </div>
      )}
    </Drawer>
  );
}

/* ------------------------------------------------------------------ */
/*  Create                                                             */
/* ------------------------------------------------------------------ */

const CATEGORIES = [
  "People",
  "Hardware",
  "Software",
  "Access",
  "Networking",
  "Facilities",
  "Other",
];

function CreateFormDrawer({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => Promise<void>;
}) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!title.trim()) {
      setError("Give the form a name.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await api.admin.forms.create({
        title: title.trim(),
        category: category || null,
      });
      await onCreated(created.id);
    } catch (e) {
      setError(errText(e, "Could not create the form."));
      setBusy(false);
    }
  }

  return (
    <Drawer
      eyebrow="New form"
      title="Name it, then build it"
      desc="It starts as a draft, visible to nobody, and opens straight in the builder."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void submit()}
            disabled={busy}
          >
            {busy ? "Creating…" : "Create & open builder"}
          </button>
        </>
      }
    >
      <div className="ad-grid">
        <Field label="Internal name" span note="What iQuest staff sort and search by.">
          <input
            value={title}
            maxLength={200}
            placeholder="e.g. New user onboarding"
            onChange={(e) => setTitle(e.target.value)}
          />
        </Field>
        <Field label="Category" span>
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {error && (
        <div className="form-error" style={{ marginTop: 12 }}>
          {error}
        </div>
      )}
    </Drawer>
  );
}
